import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';

import { Configure } from '@/components/Configure';
import { InstantSearchStateUpdater } from '@/components/InstantSearchUpdater/InstantSearchUpdater';
import { Spacer } from '@/components/Spacer';
import { ListingPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { Default } from '@/layouts/Default';
import { getRegionFromLocale } from '@/lib/regions';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { listingActions } from '@/states/listingState';

import { usePreRenderCmsSections } from './helpers';
import type { CollectionViewProps } from './types';

export const CollectionView = ({
  algoliaFilters,
  algoliaRuleName,
  algoliaUserToken,
  collectionCmsData,
  draftMode,
  englishLocaleCode = 'EN_US',
  instantSearchProps,
  pageName,
  serverState,
}: CollectionViewProps) => {
  const liveCollectionCmsData = useContentfulLiveUpdates(collectionCmsData, {
    query: ListingPageDocument,
  });
  const { cmsSectionAll } = usePreRenderCmsSections(
    liveCollectionCmsData,
    pageName,
  );
  const { asPath, locale, push, query } = useRouter();
  const indexName = getIndexName('PRODUCT', locale);
  const lastUiState = useRef<SearchUiState | null>(null);
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_WATCHES,
  });
  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
      serverState,
      englishLocaleCode,
    );
  }, [asPath, englishLocaleCode, indexName, locale, serverState]);

  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      const queryParams = buildQueryParamsFromSearchUiState(
        uiState,
        indexName,
        query,
        serverState,
        englishLocaleCode,
      );

      if (query.slug) {
        queryParams.slug = Array.isArray(query.slug)
          ? query.slug.join('/')
          : query.slug;
      }

      const cleanPathname = asPath.split('?')[0];
      push({ pathname: cleanPathname, query: queryParams }, undefined, {
        shallow: true,
      });
    },
    [asPath, englishLocaleCode, indexName, push, query, serverState],
  );

  useEffect(() => {
    listingActions.updateListingType('WATCH');
  }, []);

  // Destructure to remove initialUiState and serverState from instantSearchProps
  // to prevent them from overriding our translated uiStateFromURL and serverState prop
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serverState: _serverStateFromProps,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={indexName}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={algoliaSearchClient}
        {...restInstantSearchProps}
      >
        <InstantSearchStateUpdater expectedUiState={uiStateFromURL} />
        <Configure
          analyticsTags={[
            'platform:web',
            'product:watches',
            'page:listing',
            'context:collection',
            'collection:' + pageName,
          ]}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          facets={['*']}
          filters={algoliaFilters}
          hitsPerPage={20}
          userToken={algoliaUserToken}
          {...(algoliaRuleName ? { query: algoliaRuleName } : {})}
        />
        {cmsSectionAll}
        <Spacer height={32} />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

CollectionView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
